import { useTranslation } from 'react-i18next';
import { Avatar } from '../../components/core';
import { ChevronRightIcon } from '../../components/core';
import { getInitials } from '../../utils/formatters';
import type { ResultRow } from './types';

interface Props {
  results: ResultRow[];
  cropTypeName: (_id: string) => string;
  regionName: (_id: string) => string;
  groupByCropType: boolean;
  collapsedGroups: Set<string>;
  onToggleGroup: (_cropTypeId: string) => void;
  onViewCustomer: (_customerId: string) => void;
  isUpcoming: boolean;
}

export function ResultCards({
  results,
  cropTypeName,
  regionName,
  groupByCropType,
  collapsedGroups,
  onToggleGroup,
  onViewCustomer,
  isUpcoming,
}: Props) {
  const { t } = useTranslation();

  // Group rows by cropTypeId (order of first appearance)
  const groups: Array<{ cropTypeId: string; rows: ResultRow[] }> = [];
  if (groupByCropType) {
    const map = new Map<string, ResultRow[]>();
    for (const row of results) {
      const existing = map.get(row.cropTypeId);
      if (existing) existing.push(row);
      else {
        map.set(row.cropTypeId, [row]);
        groups.push({ cropTypeId: row.cropTypeId, rows: map.get(row.cropTypeId)! });
      }
    }
  }

  const cardBorder    = isUpcoming ? 'border-amber-200' : 'border-green-200';
  const cardSeparator = isUpcoming ? 'border-amber-100' : 'border-green-100';

  function CustomerCard({ row, showCropType }: { row: ResultRow; showCropType: boolean }) {
    return (
      <div
        key={`${row.customer.id}|${row.cropTypeId}|${row.regionId}`}
        className={`rounded-xl border bg-white p-4 ${cardBorder}`}
      >
        <div className="flex items-center gap-3">
          <Avatar size="sm" className="shrink-0">
            {getInitials(row.customer.firstName, row.customer.lastName)}
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="font-body font-semibold text-gray-900">
              {row.customer.firstName} {row.customer.lastName}
            </p>
            <a href={`tel:${row.customer.phone}`} className="font-body text-sm text-green-700 hover:underline">{row.customer.phone}</a>
            {row.customer.address && (
              <p className="font-body truncate text-xs text-gray-400">{row.customer.address}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onViewCustomer(row.customer.id)}
            className="shrink-0 font-body text-sm font-medium text-green-700 hover:text-green-900"
          >
            {t('cropStatus.viewCustomer')}
          </button>
        </div>
        <div className={`mt-3 flex flex-wrap gap-1.5 border-t pt-2 ${cardSeparator}`}>
          {showCropType && (
            <span className="inline-flex items-center rounded-full bg-earth-light px-2.5 py-0.5 font-body text-xs font-semibold text-earth">
              {cropTypeName(row.cropTypeId)}
            </span>
          )}
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 font-body text-xs font-semibold text-green-800">
            <span aria-hidden="true">📍</span> {regionName(row.regionId)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 sm:hidden">
      {groupByCropType
        ? groups.map(({ cropTypeId, rows }) => {
            const isCollapsed = collapsedGroups.has(cropTypeId);
            const count = rows.length;
            return (
              <div key={`group-${cropTypeId}`}>
                {/* Group header */}
                <button
                  type="button"
                  onClick={() => onToggleGroup(cropTypeId)}
                  aria-expanded={!isCollapsed}
                  aria-label={isCollapsed
                    ? t('cropStatus.expandGroup', { name: cropTypeName(cropTypeId) })
                    : t('cropStatus.collapseGroup', { name: cropTypeName(cropTypeId) })}
                  className="mb-2 flex w-full items-center gap-2"
                >
                  <span className={`block h-4 w-4 flex-shrink-0 text-earth transition-transform duration-200 ${isCollapsed ? 'rtl:rotate-180' : 'rotate-90'}`}>
                    <ChevronRightIcon />
                  </span>
                  <span className="inline-flex items-center rounded-full bg-earth-light px-2.5 py-0.5 font-body text-xs font-bold text-earth">
                    {cropTypeName(cropTypeId)}
                  </span>
                  <span className="font-body text-xs text-gray-500">
                    {t('cropStatus.groupCount', { count })}
                  </span>
                </button>
                {/* Cards */}
                {!isCollapsed && (
                  <div className="flex flex-col gap-2">
                    {rows.map(row => (
                      <CustomerCard
                        key={`${row.customer.id}|${row.cropTypeId}|${row.regionId}`}
                        row={row}
                        showCropType={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        : results.map(row => (
            <CustomerCard
              key={`${row.customer.id}|${row.cropTypeId}|${row.regionId}`}
              row={row}
              showCropType
            />
          ))}
    </div>
  );
}
